import {sharedCampaigns} from '@cl/shared';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {
  ConditionOperatorKey,
  ConditionOperatorKeyToText,
  ConditionType,
} from 'common/src/modules/conditions/conditions.types';
import {DTConditionNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {MacroItemTypeValue} from 'common/src/modules/macros/macros.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {ConditionCardData} from '../../scratchpad/conditionCardScratchpad.types';
import {CLStore} from 'common/src/redux/store';
import campaignVersionsApi from 'common/src/modules/campaignVersions/campaignVersions.api';
import getSignalsMetadata from './getSignalsMetadata';

function createDefaultConditionCard(): ConditionCardData {
  return {
    id: NO_NODE_ID,
    name: 'Condition Card',
    description: '',
    type: NodeType.CONDITION,
    outputs: [
      {
        id: 1,
        name: 'Fallback',
        type: OutputType.Fallback,
        nodeId: NO_NODE_ID,
        // Default to text type with empty array
        textValues: [],
      },
    ],
    inputs: [],
    nextOutputId: 1,
    categories: [],
    condKey: '',
    condKeyType: MacroItemTypeValue.Text,
    condOperator: ConditionOperatorKey.Equal,
    signalDescription: '',
    conditionType: ConditionType.Text,
    optionalOperators: [],
    shouldQueryOptionalValues: false,
  };
}

/**
 * Gets an existing condition card by ID, or creates a new default one.
 * @param cardId - The card ID to fetch, or 0/undefined for a new card
 * @returns The condition card (existing or new), fully mutable
 */
async function getOrCreateConditionCard(
  cardId: number | undefined,
  store: CLStore,
): Promise<ConditionCardData> {
  const resolvedCardId = cardId && cardId > NO_NODE_ID ? cardId : NO_NODE_ID;

  if (resolvedCardId) {
    const state = store.getState();
    const campaignId = state.campaignPage.campaignTree.campaign?.id;

    if (!campaignId) {
      throw new Error('Campaign ID not found');
    }

    const versionId = state.campaignPage.campaignTree.versionId;

    if (!versionId) {
      throw new Error('Version ID not found');
    }

    const existingCard = sharedCampaigns.nodesRef.value[resolvedCardId] as DTConditionNode;

    if (!existingCard) {
      throw new Error(`Card with id ${resolvedCardId} not found`);
    }

    /*
    We need to get the condition options for the existing card categories.
    Once we have the condition options, we need to find the matching condition.
    The matching condition is the one that has the same key and categories.
    */
    const conditions = await campaignVersionsApi.getConditionOptions({
      campaignId,
      versionId,
      categories: existingCard.categories,
    });

    const matchingCondition = conditions.find(
      (condition) => condition.value === existingCard.condKey,
    );

    if (!matchingCondition) {
      throw new Error(
        `Condition with key ${existingCard.condKey} and categories ${existingCard.categories.join(', ')} not found`,
      );
    }

    const signalsMetadata = await getSignalsMetadata(existingCard.condKey, campaignId, versionId);

    if (signalsMetadata.length === 0) {
      throw new Error(`No signals found for condition ${existingCard.condKey}`);
    }

    /*
    using the condition data, we can know the optional operators, available signals and if we need to query optional values.
    */
    return {
      ...existingCard,
      optionalOperators: (matchingCondition.optionalOperators || []).map((operator) => ({
        value: operator,
        title: ConditionOperatorKeyToText[operator as ConditionOperatorKey],
      })),
      shouldQueryOptionalValues: matchingCondition.shouldQueryOptionalValues || false,
      availableSignals: signalsMetadata,
    };
  }

  return createDefaultConditionCard();
}

export default getOrCreateConditionCard;
export {createDefaultConditionCard};
